"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { BooruGrid } from "./booru-grid"
import { Loader2, ChevronUp, ChevronDown } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import type { BooruPost, BooruSite } from "@/lib/booru-api"
import { useBooruSearch } from "@/lib/booru-store"
import { useSettingsStore } from "@/lib/settings-store"
import { useTranslations } from "next-intl"

interface InfinitePagesViewerProps {
  sites: BooruSite[]
  tags?: string[]
  negativeTags?: string[]
  rating?: "safe" | "questionable" | "explicit" | "all" | "unfiltered"
  onPostClick?: (post: BooruPost) => void
  onTagClick?: (tag: string) => void
  autoEnabled?: boolean // Indicates if this was auto-enabled for a specific source
}

interface PageData {
  pageNumber: number
  posts: BooruPost[]
  loading: boolean
  error?: string
}

export function InfinitePagesViewer({
  sites,
  tags = [],
  negativeTags = [],
  rating = "all",
  onPostClick,
  onTagClick,
  autoEnabled = false
}: InfinitePagesViewerProps) {
  const t = useTranslations("infinitePagesViewer")
  // XlecX is a source name and stays as-is; the generic fallback is translated.
  const autoEnabledForLabel = sites.includes('xlecx' as any) ? 'XlecX' : t('thisSource')
  const [currentPage, setCurrentPage] = useState(0)
  const [pages, setPages] = useState<Map<number, PageData>>(new Map())
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null)
  
  const containerRef = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef(0)
  
  const { postsPerPage } = useSettingsStore()
  
  // Search params for current page
  const searchParams = {
    tags,
    negativeTags,
    limit: postsPerPage,
    page: currentPage,
    rating,
  }
  
  // Fetch current page data
  const { posts, loading, error, hasMore } = useBooruSearch(sites, searchParams)
  
  // Update pages map when new data arrives
  useEffect(() => {
    console.log('[InfinitePagesViewer] Data update:', {
      currentPage,
      postsLength: posts.length,
      loading,
      error: error?.message,
      hasMore
    })
    
    if (posts.length > 0 || loading || error) {
      setPages(prev => {
        const newPages = new Map(prev)
        const existingPage = prev.get(currentPage)
        
        // Only update if the data has actually changed
        const hasChanged = !existingPage || 
          existingPage.posts.length !== posts.length ||
          existingPage.loading !== loading ||
          existingPage.error !== error?.message
        
        if (!hasChanged) {
          return prev // Return same reference to prevent re-render
        }
        
        newPages.set(currentPage, {
          pageNumber: currentPage,
          posts: posts,
          loading: loading,
          error: error?.message
        })
        return newPages
      })
    }
  }, [posts, loading, error, currentPage, hasMore])
  
  // Handle scroll events for page transitions
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (isTransitioning) return
    
    const container = e.currentTarget
    const scrollY = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight
    
    const scrollDelta = scrollY - lastScrollY.current
    lastScrollY.current = scrollY
    
    console.log('[InfinitePagesViewer] Scroll event:', {
      scrollY,
      scrollHeight,
      clientHeight,
      scrollDelta,
      atTop: scrollY <= 10,
      atBottom: scrollY >= scrollHeight - clientHeight - 10,
      currentPage,
      hasMore
    })
    
    // Check if we're at the top or bottom
    const atTop = scrollY <= 10
    const atBottom = scrollY >= scrollHeight - clientHeight - 10
    
    // Scrolling down at bottom - go to next page (trigger on any scroll when at bottom)
    if (atBottom && hasMore && !isTransitioning) {
      const nextPage = currentPage + 1
      console.log('[InfinitePagesViewer] At bottom, loading next page:', nextPage)
      
      setIsTransitioning(true)
      setScrollDirection('down')
      setCurrentPage(nextPage)
      
      // Reset scroll to top after transition
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = 0
        }
        setIsTransitioning(false)
        setScrollDirection(null)
      }, 300)
    } 
    // Scrolling up at top - go to previous page
    else if (atTop && scrollDelta < 0 && currentPage > 0 && !isTransitioning) {
      const prevPage = currentPage - 1
      const prevPageData = pages.get(prevPage)
      
      console.log('[InfinitePagesViewer] At top, going to previous page:', prevPage, 'has data:', !!prevPageData)
      
      if (prevPageData && prevPageData.posts.length > 0) {
        setIsTransitioning(true)
        setScrollDirection('up')
        setCurrentPage(prevPage)
        
        // Reset scroll to bottom after transition
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight
          }
          setIsTransitioning(false)
          setScrollDirection(null)
        }, 300)
      }
    }
  }, [currentPage, pages, isTransitioning, hasMore])
  
  // Manual page navigation
  const goToPage = (pageNum: number) => {
    if (pageNum < 0 || pageNum === currentPage || isTransitioning) return
    
    const targetPageData = pages.get(pageNum)
    if (!targetPageData || targetPageData.posts.length === 0) {
      // Allow going to next page even if not loaded yet
      if (pageNum > currentPage && hasMore) {
        console.log('[InfinitePagesViewer] Going to unloaded next page:', pageNum)
      } else {
        return
      }
    }
    
    setIsTransitioning(true)
    setScrollDirection(pageNum > currentPage ? 'down' : 'up')
    setCurrentPage(pageNum)
    
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0
      }
      setIsTransitioning(false)
      setScrollDirection(null)
    }, 300)
  }
  
  const currentPageData = pages.get(currentPage)
  const prevPageData = pages.get(currentPage - 1)
  
  const canGoBack = currentPage > 0 && prevPageData && prevPageData.posts.length > 0
  const canGoForward = hasMore // Can go forward if there are more posts to load
  
  return (
    <div className="flex flex-col h-full">
      {/* Page indicator and controls */}
      <div className="flex items-center justify-between p-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage - 1)}
            disabled={!canGoBack || isTransitioning}
          >
            <ChevronUp className="h-4 w-4" />
            {t("previous")}
          </Button>
          
          <span className="text-sm font-medium">
            {t("pageNumber", { number: currentPage + 1 })}
            {currentPageData && (
              <span className="text-muted-foreground ml-1">
                {t("lengthPosts", { length: currentPageData.posts.length })}
              </span>
            )}
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage + 1)}
            disabled={!canGoForward || isTransitioning}
          >
            {t("next")}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="text-xs text-muted-foreground">
          {isTransitioning ? (
            <span className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t("transitioningScrollDirection", { scrollDirection: scrollDirection ?? "" })}
            </span>
          ) : autoEnabled ? (
            <span className="text-blue-600 dark:text-blue-400">
              {t("autoEnabledForLabel", { label: autoEnabledForLabel })}
            </span>
          ) : (
            <span>
              {t("scrollToEdgesToChangePages")}
            </span>
          )}
        </div>
      </div>
      
      {/* Content area */}
      <div 
        ref={containerRef}
        className={`flex-1 overflow-y-auto transition-opacity duration-300 ${
          isTransitioning ? 'opacity-50' : 'opacity-100'
        }`}
        onScroll={handleScroll}
      >
        {currentPageData ? (
          <>
            {currentPageData.loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : currentPageData.error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-destructive mb-2">{t("errorLoadingPage")}</p>
                  <p className="text-sm text-muted-foreground">{currentPageData.error}</p>
                </div>
              </div>
            ) : currentPageData.posts.length > 0 ? (
              <BooruGrid
                posts={currentPageData.posts}
                onPostClick={onPostClick}
                onTagClick={onTagClick}
              />
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">{t("noPostsFound")}</p>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}
        
        {/* Scroll indicators */}
        {!isTransitioning && (
          <>
            {canGoBack && (
              <div className="sticky top-0 left-0 right-0 h-0 pointer-events-none">
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-background/80 backdrop-blur rounded-full px-3 py-1 text-xs text-muted-foreground border">
                  {t("scrollUpForPreviousPage")}
                </div>
              </div>
            )}
            
            {canGoForward && (
              <div className="sticky bottom-0 left-0 right-0 h-0 pointer-events-none">
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-background/80 backdrop-blur rounded-full px-3 py-1 text-xs text-muted-foreground border">
                  {t("scrollDownForNextPage")}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}